"use client";
import cn from "classnames";
import { Link } from "components/shared/Link";
import styles from "./Pagination.module.scss";
import { SpriteIcon } from "components/UI/SpriteIcon";
import arrowRightIcon from "/public/img/icons/arrow-right-thin.svg";
import { useCallback, useMemo } from "react";
import { usePathname } from "next/navigation";
import { range } from "lodash";

type PaginationProps = {
  currentPage: number | null;
  pagesCount: number;
  baseUrl: string;
};

export const Pagination = ({ baseUrl, currentPage, pagesCount }: PaginationProps) => {
  const currentPageValue = currentPage || 1;

  const pathname = usePathname();

  const urlArr = useMemo(() => pathname.split("?"), [pathname]);

  const getUrl = useCallback(
    (page: number) => {
      let url = page <= 1 ? baseUrl : `${baseUrl}/${page}`;
      if (urlArr.length === 2) {
        url += "?" + urlArr[1];
      }
      return url;
    },
    [baseUrl, urlArr]
  );
  //   EXAMPLE:
  //    {/* 1! 2  3  .  8 */} 5
  //    {/* 1  2! 3  .  8 */} 5
  //    {/* 1  2  3! 4   .  8 */} 6
  //    {/* 1  .  3  4! 5  .  8 */} 7
  //    {/* 1  .  4  5! 6  .  8 */} 7
  //    {/* 1  .  5  6! 7  8 */} 6
  //    {/* 1  .  6  7! 8 */} 5
  //    {/* 1  .  6  7  8! */} 5

  const defineItems = () => {
    if (pagesCount > 5) {
      if (currentPageValue < 3 || currentPageValue >= pagesCount - 1) {
        return range(1, 6).map((index) => {
          if (currentPageValue < 3) {
            return index < 4 ? index : index === 5 ? pagesCount : 0;
          } else {
            return index > 2 ? pagesCount - (5 - index) : index === 1 ? 1 : 0;
          }
        });
      } else {
        if (currentPageValue === 3 || currentPageValue === pagesCount - 2) {
          return range(1, 7).map((index) => {
            if (currentPageValue === 3) {
              return index < 5 ? index : index === 6 ? pagesCount : 0;
            } else {
              return index > 2 ? pagesCount - (6 - index) : index === 1 ? 1 : 0;
            }
          });
        } else {
          return range(1, 8).map((index) => {
            if (index === 1) return index;
            if (index === 7) return pagesCount;
            if (index === 3) return currentPageValue - 1;
            if (index === 4) return currentPageValue;
            if (index === 5) return currentPageValue + 1;
            return 0;
          });
        }
      }
    } else {
      // pages count <= 5
      return range(1, pagesCount + 1);
    }
  };

  const definedItems = defineItems();

  return (
    <div className={styles.pagination}>
      <Link
        href={getUrl(currentPageValue - 1)}
        className={cn(styles.btn, styles.btnPrev, {
          [styles.disabled]: currentPageValue === 1,
        })}
      >
        <SpriteIcon icon={arrowRightIcon} />
      </Link>

      <div className={styles.nav}>
        {definedItems.map((pageIndex, index) =>
          pageIndex ? (
            <Link
              key={index}
              href={getUrl(pageIndex)}
              className={cn(styles.item, {
                [styles.itemCurrent]: currentPageValue === pageIndex,
              })}
            >
              {pageIndex}
            </Link>
          ) : (
            <span key={index} className={styles.item}>
              ...
            </span>
          )
        )}
      </div>

      <Link
        href={getUrl(currentPageValue === pagesCount ? currentPageValue : currentPageValue + 1)}
        className={cn(styles.btn, styles.btnNext, {
          [styles.disabled]: currentPageValue === pagesCount,
        })}
      >
        <SpriteIcon icon={arrowRightIcon} />
      </Link>
    </div>
  );
};
